import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/faq.css";

const FAQPage = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState("quickstart");

  const handleBackClick = () => {
    navigate("/");
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setActiveSection(sectionId);
  };

  return (
    <div className="faq-container">
      <header className="faq-header">
        <button className="back-button" onClick={handleBackClick}>
          ← Back
        </button>
        <div className="logo-container">
          <a href="/">
            <img
              src="./eblogo_2.png"
              alt="ExpenseBot Logo"
              className="faq-logo"
            />
          </a>
        </div>
        <div className="faq-title-container">
          <h1 className="faq-title">ExpenseBot Guide & FAQ</h1>
        </div>
      </header>

      {/* Navigation Menu */}
      <nav className="faq-nav">
        <button
          className={`nav-item ${
            activeSection === "quickstart" ? "active" : ""
          }`}
          onClick={() => scrollToSection("quickstart")}
        >
          Quick Start
        </button>

        <button
          className={`nav-item ${activeSection === "faq" ? "active" : ""}`}
          onClick={() => scrollToSection("faq")}
        >
          FAQ
        </button>
        <button
          className={`nav-item ${activeSection === "tips" ? "active" : ""}`}
          onClick={() => scrollToSection("tips")}
        >
          Tips & Best Practices
        </button>
        <button
          className={`nav-item ${activeSection === "privacy" ? "active" : ""}`}
          onClick={() => scrollToSection("privacy")}
        >
          Privacy & Security
        </button>
        <button
          className={`nav-item ${activeSection === "support" ? "active" : ""}`}
          onClick={() => scrollToSection("support")}
        >
          Support
        </button>
      </nav>
      <section id="quickstart" className="section">
        <h2 className="section-header">Quick Start Guide</h2>

        <div className="subsection">
          <h3 className="subsection-title">
            <span className="step-number">1</span> Import Receipts
          </h3>
          <div className="step-list">
            <div className="step-item1">
              <div className="step-content">
                <p className="mb-2">
                  Import your receipts using one of the following options:
                </p>
                <ul className="list-disc pl-6 space-y-1 text-gray-700">
                  <li>
                    Forward an email to{" "}
                    <span className="font-mono bg-gray-100 px-1 rounded">
                      receipts@expensebot.ai
                    </span>
                  </li>
                  <li>Take a photo of a receipt</li>
                  <li>Upload photo or pdf from your device</li>
                  <li>From Google Photos or Gmail</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="step-item1">
            <div className="step-content">
              <p className="mb-2">Watch an overview :</p>
              <div
                style={{
                  position: "relative",
                  boxSizing: "content-box",
                  maxHeight: "80vh",
                  width: "100%",
                  aspectRatio: "1.75253807106599",
                  padding: "40px 0 40px 0",
                }}
              >
                <iframe
                  src="https://app.supademo.com/embed/cm95c1zmy046wy80iek98elkp?embed_v=2"
                  loading="lazy"
                  title="ExpenseBot How To"
                  allow="clipboard-write"
                  frameBorder="0"
                  webkitAllowFullScreen="true"
                  mozAllowFullScreen="true"
                  allowFullScreen
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                  }}
                ></iframe>
              </div>
            </div>
          </div>

          <h3 className="subsection-title mt-8">
            <span className="step-number">2</span> Creating Expense Reports
          </h3>
          <div className="step-list">
            <div className="step-item1">
              <div className="step-content">
                <p className="mb-2">
                  Once you have expenses in your ExpenseBot spreadsheet, use the
                  Report Wizard to create a report by business purpose using
                  your tags, then share with someone to approve or review.
                </p>
                <div
                  style={{
                    position: "relative",
                    boxSizing: "content-box",
                    maxHeight: "80vh",
                    width: "100%",
                    aspectRatio: "1.75253807106599",
                    padding: "40px 0 40px 0",
                  }}
                >
                  <iframe
                    src="https://app.supademo.com/embed/cm95zfl0m3y7reyp007ojpevy?embed_v=2"
                    loading="lazy"
                    title="Creating and Managing Reports"
                    allow="clipboard-write"
                    frameBorder="0"
                    webkitAllowFullScreen="true"
                    mozAllowFullScreen="true"
                    allowFullScreen
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                    }}
                  ></iframe>
                </div>
              </div>
            </div>
          </div>
          <div className="important-note">
            Important: Add info@expensebot.ai to your safe sender list to
            receive processing notifications
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="section">
        <h2 className="section-header">Frequently Asked Questions</h2>

        {/* Import & Processing Questions */}
        <div className="subsection">
          <h3 className="subsection-title">Import & Processing</h3>
          <div class="faq-item">
            <p class="faq-question">
              Do I need a Gmail account to use ExpenseBot?
            </p>
            <div class="faq-answer">
              <p>
                ExpenseBot works with different types of email accounts
                depending on your role:
              </p>
              <ul class="list-disc pl-6 space-y-2 mb-4">
                <li>
                  <strong>For Expense Tracking Users:</strong> You need a Google
                  account to sign in and use ExpenseBot's tracking features. If
                  you don't have a Google account, you can easily create one
                  using your existing email address (Yahoo, Outlook, work email,
                  etc.).
                </li>
                <li>
                  <strong>For Reviewers/Approvers:</strong> You can use ANY
                  email address to review and approve reports - no Google
                  account required! When someone shares a report with you,
                  you'll receive an email with a link, and you can access it
                  directly.
                </li>
              </ul>

              <div class="important-note">
                <strong>Don't have a Google account?</strong> You can create one
                using your existing email address in just a few steps:
                <ol class="list-decimal pl-6 space-y-2 mt-2">
                  <li>
                    Go to the{" "}
                    <a
                      href="https://accounts.google.com/signup"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Google Account Sign-up page
                    </a>
                  </li>
                  <li>Enter your name and other basic information</li>
                  <li>Then you'll be asked to Choose your Gmail address</li>
                  <li>
                    Click "Use your email address" instead of creating a Gmail
                    address
                  </li>
                  <li>Enter your current email address and click "Next"</li>
                  <li>Verify your email with the code Google sends you</li>
                </ol>
                This creates a Google account linked to your existing email
                address, giving you access to ExpenseBot and all Google services
                without creating a new email address.
              </div>
            </div>
          </div>
          <div className="faq-item">
            <p className="faq-question">
              How do I take a photo of a receipt and submit to ExpenseBot?
            </p>
            <div className="faq-answer">
              <div className="relative w-full overflow-hidden pt-[56.25%] rounded-lg shadow-md">
                <iframe
                  className="absolute top-0 left-0 w-full h-full"
                  src="https://www.youtube.com/embed/Gpx74dqoYXQ?si=c0Rz8YDG2ogNAZI9&rel=0"
                  title="How to use ExpenseBot with Google Photos"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  referrerPolicy="strict-origin-when-cross-origin"
                />
              </div>
            </div>
          </div>

          <div className="faq-item">
            <p className="faq-question">
              How can I import photos of receipts from my Google Photos?
            </p>
            <div className="faq-answer">
              <p className="mb-4">
                ExpenseBot makes it easy to find and import receipts directly
                from your Google Photos account:
              </p>
              <div
                style={{
                  position: "relative",
                  boxSizing: "content-box",
                  maxHeight: "80svh",
                  width: "100%",
                  aspectRatio: "1.75253807106599",
                  padding: "40px 0 40px 0",
                }}
              >
                <iframe
                  src="https://app.supademo.com/embed/cm9bsndib00661c0jwzkytms3?embed_v=2"
                  loading="lazy"
                  title="Import from Google Photos"
                  allow="clipboard-write"
                  frameBorder="0"
                  webkitAllowFullScreen="true"
                  mozAllowFullScreen="true"
                  allowFullScreen
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                  }}
                ></iframe>
              </div>
            </div>
          </div>

          <div className="faq-item">
            <p className="faq-question">
              How can I import receipts from my Gmail inbox?
            </p>
            <div className="faq-answer">
              <p className="mb-4">
                ExpenseBot offers easy integration with Gmail to import your
                emails of receipts in just a click:
              </p>
              <div
                style={{
                  position: "relative",
                  boxSizing: "content-box",
                  maxHeight: "80svh",
                  width: "100%",
                  aspectRatio: "1.8267195767195767",
                  padding: "40px 0 40px 0",
                }}
              >
                <iframe
                  src="https://app.supademo.com/embed/cm9bt0i9l3ku4pxcbm7t48s4t?embed_v=2"
                  loading="lazy"
                  title="Import Receipts from Gmail"
                  allow="clipboard-write"
                  frameBorder="0"
                  webkitAllowFullScreen="true"
                  mozAllowFullScreen="true"
                  allowFullScreen
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                  }}
                ></iframe>
              </div>
            </div>
          </div>

          <div className="faq-item">
            <p className="faq-question">
              How do I import screenshots and images from my device?
            </p>
            <div className="faq-answer">
              <p className="mb-4">
                If you have screenshots, saved images, or other receipt photos
                stored directly on your device (not in Google Photos), you can
                import multiple images at once:
              </p>
              <div
                style={{
                  position: "relative",
                  boxSizing: "content-box",
                  maxHeight: "80vh",
                  width: "100%",
                  aspectRatio: "1.75253807106599",
                  padding: "40px 0 40px 0",
                }}
              >
                <iframe
                  src="https://app.supademo.com/embed/cm95rvyew3vryeyp015s7wq6m?embed_v=2"
                  loading="lazy"
                  title="Importing Images from Device"
                  allow="clipboard-write"
                  frameBorder="0"
                  webkitAllowFullScreen="true"
                  mozAllowFullScreen="true"
                  allowFullScreen
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                  }}
                ></iframe>
              </div>
            </div>
          </div>

          <div className="faq-item">
            <p className="faq-question">
              How do I import multiple PDFs from my device?
            </p>
            <div className="faq-answer">
              <div
                style={{
                  position: "relative",
                  boxSizing: "content-box",
                  maxHeight: "80vh",
                  width: "100%",
                  aspectRatio: "1.75253807106599",
                  padding: "40px 0 40px 0",
                }}
              >
                <iframe
                  src="https://app.supademo.com/embed/cm95ru89x3vqqeyp0bhgqcsfc?embed_v=2"
                  loading="lazy"
                  title="Importing PDFs"
                  allow="clipboard-write"
                  frameBorder="0"
                  webkitAllowFullScreen="true"
                  mozAllowFullScreen="true"
                  allowFullScreen
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                  }}
                ></iframe>
              </div>
            </div>
          </div>

          <div className="faq-item">
            <p className="faq-question">
              How do I submit receipts I receive by email?
            </p>
            <div className="faq-answer">
              <p className="mb-4">
                You can easily forward any receipt emails to ExpenseBot. Simply
                forward your email to receipts@expensebot.ai and ExpenseBot will
                automatically process:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>PDF attachments containing receipts</li>
                <li>Receipt details embedded directly in the email body</li>
                <li>Image attachments of receipts</li>
              </ul>
              <p className="mb-4">
                All forwarded receipts will automatically appear in your
                ExpenseBot spreadsheet:
              </p>
              <div
                style={{
                  position: "relative",
                  boxSizing: "content-box",
                  maxHeight: "80vh",
                  width: "100%",
                  aspectRatio: "1.7532656023222062",
                  padding: "40px 0",
                }}
              >
                <iframe
                  src="https://app.supademo.com/embed/cm90an0fo178zeyp0dlr2zpjh?embed_v=2"
                  loading="lazy"
                  title="Email: Forward a Receipt"
                  allow="clipboard-write"
                  frameBorder="0"
                  webkitAllowFullScreen
                  mozAllowFullScreen
                  allowFullScreen
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                  }}
                ></iframe>
              </div>
            </div>
          </div>
          <div className="faq-item">
            <p className="faq-question">
              Can I forward receipts of emails from another email account other
              than the Gmail I signed up with?
            </p>
            <div className="faq-answer">
              <p>
                To forward receipts from a different email address than your
                Gmail account, add it as an Email Alias in Settings. If you
                primarily receive emails of receipts in your Gmail account, you
                can skip this step.
              </p>
              <div
                style={{
                  position: "relative",
                  boxSizing: "content-box",
                  maxHeight: "80svh",
                  width: "100%",
                  aspectRatio: "1.8267195767195767",
                  padding: "40px 0 40px 0",
                }}
              >
                <iframe
                  src="https://app.supademo.com/embed/cm9bwbdh13naypxcb4czo9jtg?embed_v=2"
                  loading="lazy"
                  title="Email Alias"
                  allow="clipboard-write"
                  frameBorder="0"
                  webkitAllowFullScreen="true"
                  mozAllowFullScreen="true"
                  allowFullScreen
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                  }}
                ></iframe>
              </div>
            </div>
          </div>

          <div className="faq-item">
            <p className="faq-question">
              Does ExpenseBot handle duplicate receipts?
            </p>
            <p className="faq-answer">
              Yes! ExpenseBot automatically detects and eliminates duplicate
              receipts to keep your reports accurate.
            </p>
          </div>

          <div className="faq-item">
            <p className="faq-question">
              Does ExpenseBot handle multi currency?
            </p>
            <p className="faq-answer">
              Yes, we handle currency from any country and convert it back to
              your home currency using the exchange rate from the date of the
              receipt itself.
            </p>
          </div>
        </div>
        <div className="faq-item">
          <p className="faq-question">
            How do I reconcile my credit card statement with my expenses?
          </p>
          <div className="faq-answer">
            <p className="mb-4">
              ExpenseBot's Credit Card Reconciliation feature helps you match
              your credit card statement with your expenses, finding missing
              receipts and updating exchange rates:
            </p>
            <div
              style={{
                position: "relative",
                boxSizing: "content-box",
                maxHeight: "80vh",
                width: "100%",
                aspectRatio: "1.8240740740740742",
                padding: "40px 0 40px 0",
              }}
            >
              <iframe
                src="https://app.supademo.com/embed/cma93q9zx5soh13m0jrbaksw6?embed_v=2"
                loading="lazy"
                title="Credit Card Reconciliation"
                allow="clipboard-write"
                frameBorder="0"
                webkitAllowFullScreen="true"
                mozAllowFullScreen="true"
                allowFullScreen
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                }}
              ></iframe>
            </div>
          </div>
        </div>
        {/* Reports & Organization */}
        <div className="subsection">
          <h3 className="subsection-title">Reports & Organization</h3>

          <div className="faq-item">
            <p className="faq-question">
              How do I create a tailored expense report for a particular trip,
              project or client?
            </p>
            <div className="faq-answer">
              <p className="mb-4">
                Use tags to group expenses, then create specialized reports
                using the Report Wizard:
              </p>
              <div
                style={{
                  position: "relative",
                  boxSizing: "content-box",
                  maxHeight: "80vh",
                  width: "100%",
                  aspectRatio: "1.75253807106599",
                  padding: "40px 0 40px 0",
                }}
              >
                <iframe
                  src="https://app.supademo.com/embed/cm95zfl0m3y7reyp007ojpevy?embed_v=2"
                  loading="lazy"
                  title="Creating and Managing Reports"
                  allow="clipboard-write"
                  frameBorder="0"
                  webkitAllowFullScreen="true"
                  mozAllowFullScreen="true"
                  allowFullScreen
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                  }}
                ></iframe>
              </div>
            </div>
          </div>
          <div className="faq-item">
            <p className="faq-question">How do I delete a report?</p>
            <div className="faq-answer">
              <p className="mb-4">
                You can delete a report or undo a report using the Report
                Wizard:
              </p>
              <div
                style={{
                  position: "relative",
                  boxSizing: "content-box",
                  maxHeight: "80vh",
                  width: "100%",
                  aspectRatio: "1.75253807106599",
                  padding: "40px 0 40px 0",
                }}
              >
                <iframe
                  src="https://app.supademo.com/embed/cm97421584m9beyp0pm1mgbww?embed_v=2"
                  loading="lazy"
                  title="Undo / Delete a Report"
                  allow="clipboard-write"
                  frameBorder="0"
                  webkitAllowFullScreen="true"
                  mozAllowFullScreen="true"
                  allowFullScreen
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                  }}
                ></iframe>
              </div>
            </div>
          </div>
          <div className="faq-item">
            <p className="faq-question">
              How do I approve expense reports as a manager or accountant?
            </p>
            <div className="faq-answer">
              <p className="mb-4">
                When you receive an email that someone has granted you access to
                their report, here's what you can do:
              </p>
              <div
                style={{
                  position: "relative",
                  boxSizing: "content-box",
                  maxHeight: "80vh",
                  width: "100%",
                  aspectRatio: "1.75253807106599",
                  padding: "40px 0 40px 0",
                }}
              >
                <iframe
                  src="https://app.supademo.com/embed/cm96153nj3ys9eyp0zwld5vx0?embed_v=2"
                  loading="lazy"
                  title="Approving a Report"
                  allow="clipboard-write"
                  frameBorder="0"
                  webkitAllowFullScreen="true"
                  mozAllowFullScreen="true"
                  allowFullScreen
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                  }}
                ></iframe>
              </div>
            </div>
          </div>
          <div className="faq-item">
            <p className="faq-question">
              As a reviewer, how do I communicate with the submitter about
              expenses in a report?
            </p>
            <div className="faq-answer">
              <p className="mb-4">
                As a reviewer or approver, you can easily leave comments on
                individual expenses or add notes about the entire report to
                communicate with the submitter:
              </p>
              <div
                style={{
                  position: "relative",
                  boxSizing: "content-box",
                  maxHeight: "80vh",
                  width: "100%",
                  aspectRatio: "1.8240740740740742",
                  padding: "40px 0 40px 0",
                }}
              >
                <iframe
                  src="https://app.supademo.com/embed/cma1j0sri1q4813m026w5mrwp?embed_v=2"
                  loading="lazy"
                  title="Mentions"
                  allow="clipboard-write"
                  frameBorder="0"
                  webkitAllowFullScreen="true"
                  mozAllowFullScreen="true"
                  allowFullScreen
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                  }}
                ></iframe>
              </div>
            </div>
          </div>
          <div className="faq-item">
            <p className="faq-question">
              How do I share my expense report with a manager or accountant?
            </p>
            <div className="faq-answer">
              <p className="mb-4">
                Share your report quickly with your manager or accountant with
                our One-Click Sharing feature:
              </p>
              <div
                style={{
                  position: "relative",
                  boxSizing: "content-box",
                  maxHeight: "80vh",
                  width: "100%",
                  aspectRatio: "1.8245083207261725",
                  padding: "40px 0 40px 0",
                }}
              >
                <iframe
                  src="https://app.supademo.com/embed/cm9j58ysw366bljv5jy399h4b?embed_v=2"
                  loading="lazy"
                  title="One-Click Sharing"
                  allow="clipboard-write"
                  frameBorder="0"
                  webkitAllowFullScreen="true"
                  mozAllowFullScreen="true"
                  allowFullScreen
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                  }}
                ></iframe>
              </div>
            </div>
          </div>
          <div className="faq-item">
            <p className="faq-question">
              How do I customize expense categories?
            </p>
            <div className="faq-answer">
              <p className="mb-4">
                ExpenseBot automatically determines the correct expense category
                account. Here's how you can customize them:
              </p>
              <div
                style={{
                  position: "relative",
                  boxSizing: "content-box",
                  maxHeight: "80vh",
                  width: "100%",
                  aspectRatio: "1.75253807106599",
                  padding: "40px 0 40px 0",
                }}
              >
                <iframe
                  src="https://app.supademo.com/embed/cm95rqwna3vokeyp01fl0aozf?embed_v=2"
                  loading="lazy"
                  title="Expense Categories"
                  allow="clipboard-write"
                  frameBorder="0"
                  webkitAllowFullScreen="true"
                  mozAllowFullScreen="true"
                  allowFullScreen
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                  }}
                ></iframe>
              </div>
            </div>
          </div>
          <div className="faq-item">
            <p className="faq-question">
              How do I manage tags to organize my expenses?
            </p>
            <div className="faq-answer">
              <p className="mb-4">
                Tags let you group expenses by business trip, client, project so
                you can create separate reports for different purposes:
              </p>
              <div
                style={{
                  position: "relative",
                  boxSizing: "content-box",
                  maxHeight: "80vh",
                  width: "100%",
                  aspectRatio: "1.75253807106599",
                  padding: "40px 0 40px 0",
                }}
              >
                <iframe
                  src="https://app.supademo.com/embed/cm95rsd9d3vpceyp0tumybxm5?embed_v=2"
                  loading="lazy"
                  title="Tags"
                  allow="clipboard-write"
                  frameBorder="0"
                  webkitAllowFullScreen="true"
                  mozAllowFullScreen="true"
                  allowFullScreen
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                  }}
                ></iframe>
              </div>
            </div>
          </div>
          <div className="faq-item">
            <p className="faq-question">
              How do I add my Employee ID to my report?
            </p>
            <div className="faq-answer">
              <p>
                To add your Employee ID, go to your main ExpenseBot spreadsheet,
                click on the
                <strong> Settings</strong> tab, and enter your Employee ID in
                cell <strong>B2</strong>. Thats it, now all of your reports will
                show your Employee ID.
              </p>
            </div>
          </div>
          {/*
          <div className="faq-item">
            <p className="faq-question">
              Can I group expenses by client to bill-back?
            </p>
            <div className="faq-answer">
              <p className="mb-4">
                Yes, you can. Here's an end-to-end video walkthrough:
              </p>
              <div className="relative w-full overflow-hidden bg-gray-100 rounded-xl aspect-video">
                <iframe
                  className="absolute top-0 left-0 w-full h-full"
                  src="https://www.youtube.com/embed/WrlkZv7LOFA?si=GD7pNMVF34oKrHby?rel=0"
                  title="How to handle emailed receipts"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  referrerPolicy="strict-origin-when-cross-origin"
                />
              </div>
            </div>
          </div>
*/}
        </div>

        {/* Policy & Compliance */}
        <div className="subsection">
          <h3 className="subsection-title">Policy & Integrations</h3>

          <div className="faq-item">
            <p className="faq-question">
              How does the ExpenseBot AI determine compliance with Expense
              Policies?
            </p>
            <p className="faq-answer">
              Upload your policy pdf, and ExpenseBot can evaluate any report
              instantly against it.
            </p>
            <div
              style={{
                position: "relative",
                boxSizing: "content-box",
                maxHeight: "80vh",
                width: "100%",
                aspectRatio: "1.7563",
                padding: "40px 0",
              }}
            >
              <iframe
                src="https://app.supademo.com/embed/cm79jgz4l0kjs11ono5aqsqj6?embed_v=2"
                loading="lazy"
                title="Automated Expense Policy Compliance"
                allow="clipboard-write"
                frameBorder="0"
                webkitAllowFullScreen
                mozAllowFullScreen
                allowFullScreen
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                }}
              ></iframe>
            </div>
          </div>

          <div className="faq-item">
            <p className="faq-question">Can I connect QuickBooks?</p>
            <div className="faq-answer">
              <p className="mb-4">
                Yes, you or your accounting professional can submit your reports
                to QuickBooks.
              </p>
              <a
                href="https://www.expensebot.ai/quickbooksintegration"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <svg
                  className="w-5 h-5 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  />
                </svg>
                View QuickBooks Integration Guide
              </a>
            </div>
          </div>

          <div className="faq-item">
            <p className="faq-question">Do I need an app?</p>
            <p className="faq-answer">
              No, just sign in at https://www.expensebot.ai
            </p>
          </div>
        </div>
      </section>

      {/* Tips & Best Practices Section */}
      <section id="tips" className="section">
        <h2 className="section-header">Tips & Best Practices</h2>

        {/* Receipt Tips */}
        <div className="tips-card">
          <h3 className="tips-card-title">Receipt Management</h3>
          <ul className="tips-list">
            <li className="tips-item">
              <i className="fas fa-trash-alt tips-icon"></i>
              <span className="tips-content">
                Once you submit a photo of a receipt to ExpenseBot, you can
                delete it from your camera roll.
              </span>
            </li>
            <li className="tips-item">
              <i className="fas fa-receipt tips-icon"></i>
              <div className="flex flex-col gap-2">
                <span className="tips-content">
                  For restaurants receipts, include both the itemized receipt
                  and credit card slip in the same photo to capture tax, tip and
                  total
                </span>
                <div className="mt-2 rounded-lg overflow-hidden shadow-sm">
                  <img
                    src="/sidebyside.png"
                    alt="Example of itemized receipt next to credit card slip"
                    className="w-full object-cover rounded-lg hover:opacity-95 transition-opacity"
                    style={{ maxWidth: "300px" }}
                  />
                </div>
              </div>
            </li>
            <li className="tips-item">
              <i className="fas fa-pen tips-icon"></i>
              <div className="space-y-3 w-full">
                <span className="tips-content">
                  Write Tag names directly on receipts before photographing for
                  auto-grouping by client, project or trip
                </span>
              </div>
            </li>
          </ul>
        </div>

        {/* Organization Tips */}
        <div className="tips-card">
          <h3 className="tips-card-title">Organization Best Practices</h3>
          <ul className="tips-list">
            <li className="tips-item">
              <i className="fas fa-user-plus tips-icon"></i>
              <div className="w-full space-y-4">
                <div className="tips-content">
                  Invite someone to approve or review your reports
                </div>
                <div className="space-y-4"></div>
              </div>
            </li>
            <li className="tips-item">
              <i className="fas fa-highlighter tips-icon"></i>
              <div className="flex flex-col gap-2">
                <span className="tips-content">
                  Review yellow highlighted cells to check AI's suggestions
                </span>
                <div className="mt-2 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                  <img
                    src="/yellow.png"
                    alt="Example of yellow highlighted cells for AI suggestions"
                    className="w-full object-cover rounded-lg"
                    style={{ maxWidth: "300px" }}
                  />
                </div>
              </div>
            </li>
            <li className="tips-item">
              <i className="fas fa-folder tips-icon"></i>
              <span className="tips-content">
                All ExpenseBot data is stored in your Google Drive "ExpenseBot
                Expenses" folder
              </span>
            </li>
          </ul>
        </div>

        <div className="important-note">
          Need help? Email support@expensebot.ai
        </div>
      </section>

      {/* Privacy & Security Section */}
      <section id="privacy" className="section">
        <h2 className="section-header">Privacy & Security</h2>

        <div className="subsection">
          <div className="faq-item">
            <p className="faq-question">How does ExpenseBot handle my data?</p>
            <p className="faq-answer">
              ExpenseBot uses a <strong>privacy-first</strong> approach. Unlike
              most apps, ExpenseBot does not store your data. All data (photos
              and spreadsheets) is stored on your own Google Drive, accessible
              only by you.
            </p>
          </div>

          <div className="faq-item">
            <p className="faq-question">
              Can I reset my account or delete my data?
            </p>
            <p className="faq-answer">
              Yes, under <strong>Settings</strong>, choose{" "}
              <strong>Reset Account</strong> to delete the ExpenseBot folder on
              your Drive and start fresh. This won't affect your subscription
              status.
            </p>
          </div>
        </div>
      </section>
      {/* Support Section */}
      <section id="support" className="section">
        <h2 className="section-header">Support</h2>

        <div className="subsection">
          <h3 className="subsection-title">How to Get Help</h3>

          <div className="support-container">
            <div className="support-card">
              <div className="support-icon">
                <i className="fas fa-envelope"></i>
              </div>
              <h4 className="support-title">Email Support</h4>
              <p className="support-description">
                Our dedicated support team is available Monday through Friday, 9
                AM to 6 PM EST.
              </p>
              <a
                href="mailto:support@expensebot.ai"
                className="cta-button support-button"
              >
                Contact Support: support@expensebot.ai
              </a>
            </div>

            <div className="support-card">
              <div className="support-icon">
                <i className="fas fa-book"></i>
              </div>
              <h4 className="support-title">Documentation</h4>
              <p className="support-description">
                Browse our comprehensive guides and tutorials in the sections
                above.
              </p>
            </div>
          </div>

          <div className="support-response">
            <h4 className="response-title">Response Times</h4>
            <p>
              We typically respond to all support inquiries within 24 business
              hours.
            </p>
            <p>
              For urgent matters, please include "URGENT" in your email subject
              line.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQPage;
